import { Injectable, OnDestroy } from '@angular/core';
import { differenceInSeconds, startOfSecond } from 'date-fns';
import { BehaviorSubject, combineLatest, race, Subject, throwError } from 'rxjs';
import { catchError, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { CompanyContextService } from 'src/app/shared/company-context.service';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import {
  PollingService_FetchEntitiesGQL,
  PollingService_FetchEntitiesQueryVariables,
  UserRole,
} from 'src/graphql/generated/graphql.generated';
import { StoreService } from 'src/store/store.service';
import { TickService } from 'src/app/shared/tick.service';
import { firstValueOrUndefined, undefinedOrSwitchMap } from 'src/util/rxjs';
import { selectAllCompanies } from 'src/store/entityStores/entity-store-selectors';
import { cacheFactory } from 'src/util/cache';
import { AppContext, AppContextService } from 'src/app/shared/app-context/app-context.service';
import { UserService } from '../tcs-identity/user.service';
import { AuthenticationService } from '../tcs-identity/authentication.service';
import { RegionContextService } from '../region-context-service/region-context.service';
import { TabsService } from '../tcs-tabs/tabs.service';

type QueryVariables = PollingService_FetchEntitiesQueryVariables;

interface Metadata {
  cardSummary: { maxLastUpdateDate?: Date };
  vehicleSummary: { maxLastUpdateDate?: Date };
  unitSummary: { maxLastUpdateDate?: Date };
  vehiclePositionSummary: { maxLastUpdateDate?: Date };
}

interface Flag {
  canFetch: boolean;
}

interface TriggerFlags {
  passdownLogs?: Flag;
  questionnaireResponses?: Flag;
  notifications?: Flag;
  summaries?: Flag;
  vehiclePositionSummaries?: Flag;
}

type TriggerFlagTracker = keyof TriggerFlags;

const timedTriggers: { trigger: keyof TriggerFlags; intervalSeconds: number; lastRun?: Date }[] = [
  {
    trigger: 'questionnaireResponses',
    intervalSeconds: 300,
  },
  {
    trigger: 'passdownLogs',
    intervalSeconds: 300,
  },
  {
    trigger: 'notifications',
    intervalSeconds: 120,
  },
  {
    trigger: 'summaries',
    intervalSeconds: 60,
  },
  {
    trigger: 'vehiclePositionSummaries',
    intervalSeconds: 60,
  },
];

const buildTriggerFlags = ({
  fetchAll = false,
}: {
  fetchAll?: boolean;
} = {}): TriggerFlags => {
  const now = startOfSecond(new Date());
  const flags = timedTriggers.reduce((triggers, cur) => {
    const diff = differenceInSeconds(now, cur.lastRun ?? now);
    const canFetch = fetchAll || diff >= cur.intervalSeconds;
    triggers[cur.trigger] = { canFetch };
    cur.lastRun = canFetch || cur.lastRun == null ? now : cur.lastRun;
    return triggers;
  }, {} as TriggerFlags);
  return flags;
};

@Injectable({
  providedIn: 'root',
})
export class PollingService implements OnDestroy {
  public qaResponseAwaitingReviewCount$ = new BehaviorSubject<number>(0);
  public passdownLogCount$ = new BehaviorSubject<number>(0);
  private _onDestroy$ = new Subject();
  private _enabledAppContexts = [AppContext.Ops, AppContext.FleetStatus];
  private readonly _queryParamsTracker = cacheFactory.accessTracker<TriggerFlagTracker>();
  private readonly _cache = cacheFactory.keyValueCache<QueryVariables, Subject<any>>(60_000 * 30);

  constructor(
    private _storeService: StoreService,
    private readonly _log: Logger,
    private _gql: PollingService_FetchEntitiesGQL,
    private _user: UserService,
    private _company: CompanyContextService,
    private _region: RegionContextService,
    private _auth: AuthenticationService,
    private _tick: TickService,
    private _appContext: AppContextService,
    private _tabsService: TabsService,
  ) {}

  public init() {
    combineLatest([
      this._appContext.currentContext$,
      this._tabsService.isTabActiveOrPending$,
      this._tick.seconds$,
    ])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([appContext, isTabActive]) => {
        if (this._enabledAppContexts.includes(appContext) === true && isTabActive === true) {
          const flags = buildTriggerFlags();
          const canFetchData =
            Object.values(flags).filter((value?: Flag) => value?.canFetch === true).length > 0;
          if (canFetchData) {
            this.fetch(flags);
          }
        }
      });
  }

  public async fetch(flags: TriggerFlags) {
    return await firstValueOrUndefined(this._fetch(flags));
  }

  public _fetch(triggerFlags?: TriggerFlags) {
    const companyId$ = this._company.company$.pipe(
      take(1),
      map((c) => c?.id ?? null),
    );
    const currRegionId$ = this._region.currentRegionId$.pipe(
      take(1),
      map((r) => r ?? null),
    );
    const hasRole$ = this._user.hasAnyRoleAnyScope([UserRole.Communications]);
    const allCompanyIds$ = this._storeService.store.select(selectAllCompanies).pipe(
      take(1),
      map((companies) =>
        companies
          .valueSeq()
          .map((c) => c.id)
          .toArray(),
      ),
    );

    return combineLatest([
      this._auth.isAuthenticated$.pipe(take(1)),
      hasRole$,
      companyId$,
      allCompanyIds$,
      currRegionId$,
    ]).pipe(
      map(([isAuthenticated, hasRole, companyId, allCompanyIds, regionId]) => {
        if (!isAuthenticated) {
          return undefined;
        }
        const flags = triggerFlags != null ? triggerFlags : buildTriggerFlags({ fetchAll: true });
        const userRoles =
          hasRole === true
            ? [UserRole.Communications]
            : this._user.user?.userRoles?.toArray() == null
            ? null
            : this._user.user?.userRoles?.toArray();

        return this._createQueryParams({ flags, companyId, regionId, userRoles, allCompanyIds });
      }),
      undefinedOrSwitchMap((params) => {
        // get lastAccess trackers
        const summariesTracker = this._getAccessTracker(
          'summaries',
          params.includeSummaries ?? false,
        );
        const vehiclePositionsTracker = this._getAccessTracker(
          'vehiclePositionSummaries',
          params.includeVehiclePositionSummaries ?? false,
        );

        // cancel previous query on matching params
        const cancel$ = this._cache.has(params)
          ? this._cache.getUnsafe(params)
          : this._cache.set(params, new Subject());
        cancel$.next(null);
        const cancelled$ = race(this._onDestroy$, cancel$);

        // setup for rollback
        const rollbackLastAccessDate = () => {
          summariesTracker.rollback();
          vehiclePositionsTracker.rollback();
        };
        const requestState = { completed: false, cancelled: false, error: false };

        // execute query
        return this._storeService
          .upsertEntitiesFromQuery({
            query: this._gql,
            variables: {
              ...params,
              vehiclesFetchAfterDate: summariesTracker.lastAccessDate,
              unitsFetchAfterDate: summariesTracker.lastAccessDate,
              cardsFetchAfterDate: summariesTracker.lastAccessDate,
              vehiclePositionSummariesFetchAfterDate: vehiclePositionsTracker.lastAccessDate,
            },
            isCancellable: true,
          })
          .pipe(
            filter((r) => r != null && r.data != null),
            map((r) => r.data),
            tap({
              next: (response) => {
                requestState.completed = true;
                if (params.includeQuestionnaireResponses ?? false) {
                  const awaitingReviewQAResponseCount =
                    response?.questionnaireResponses?.all.meta.total;
                  this.qaResponseAwaitingReviewCount$.next(awaitingReviewQAResponseCount ?? 0);
                }
                if (params.includePassdownLogs ?? false) {
                  const awaitingPassdownLogCount = response?.passdownLogs?.all.meta.total;
                  this.passdownLogCount$.next(awaitingPassdownLogCount ?? 0);
                }
              },
              unsubscribe: () => (requestState.cancelled = !requestState.completed),
              error: () => (requestState.error = true),
              finalize: () => {
                if (requestState.cancelled || requestState.error) {
                  rollbackLastAccessDate();
                }
              },
            }),
            takeUntil(cancelled$),
          );
      }),
      catchError((err) => {
        this._log.error(`Polling error fetching data: ${err.message}`);
        return throwError(() => err);
      }),
    );
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }

  private _getAccessTracker(id: keyof TriggerFlags, canFetch: boolean) {
    if (canFetch) {
      return this._queryParamsTracker.get(id);
    } else {
      return this._queryParamsTracker.peek(id);
    }
  }

  private _createQueryParams({
    flags,
    regionId,
    companyId,
    userRoles,
    allCompanyIds,
  }: {
    flags: TriggerFlags;
    regionId: string | null;
    companyId: string | null;
    userRoles: UserRole[] | null;
    allCompanyIds: string[];
  }): QueryVariables {
    const includeSummaries = true;
    const includeQuestionnaireResponses = flags.questionnaireResponses?.canFetch;
    const includePassdownLogs = (flags.passdownLogs?.canFetch ?? false) && regionId != null;
    const includeVehiclePositionSummaries = flags.vehiclePositionSummaries?.canFetch ?? true;
    return {
      companyId,
      regionId,
      userRoles,
      includeQuestionnaireResponses,
      includeVehiclePositionSummaries,
      includePassdownLogs,
      includeSummaries,
      companyIds: allCompanyIds ?? [],
      vehiclesFetchAfterDate: null,
      unitsFetchAfterDate: null,
      cardsFetchAfterDate: null,
      vehiclePositionSummariesFetchAfterDate: null,
    };
  }
}
